import { useState, useRef } from 'react'
import { Button, Col, Collapse, Form, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useField, useFormikContext } from 'formik'
import { camelCase, kebabCase } from 'lodash'
import * as Yup from 'yup'
import { Orientation } from '../../common/types'
import { createSimpleValidation, fieldNameWithParent } from './common'
import { useFormikReset, Col0, DirtyEffect, NoEffect, Row0 } from '../CommonComponents'
import { stripTags } from '../text'
import { coveringHeadingTypes, normalHeadingTypes, getCoveringMarginCharacters } from '../ArticleParse'
import { TagEditor } from '..'
import type { Article, LayoutableSetting } from '../types'

interface HeadingData {
  content?: string
  taggedContent?: string | null
  orientation?: string
  headingType?: string
  numberOfOccupyingColumns?: number | string | null
  numberOfOccupyingRows?: number | string | null
  numberOfOccupyingCharacters?: number | string | null
  headingObjectStyleType?: string
  id?: string
  useDesignedHeading?: boolean
}

interface HeadingBase {
  menuType: string
  type: string
  tagText: string
}

interface HeadingItem {
  data?: HeadingData
  base: HeadingBase
}

interface FormValues {
  headings: Record<string, HeadingItem>
}

export interface CommonProps {
  article: Partial<Article>
  layoutable: { setting: LayoutableSetting }
  formEditable: boolean
}

interface Props extends CommonProps {
  name: string
}

interface HeadingContentMoveFieldProps extends CommonProps {
  name: string
}

interface HeadingContentEditFieldProps {
  disabled: boolean
  name: string
  layoutable: { setting: LayoutableSetting }
}

const blankHeadingData = (headingType: string, orientation: string) => ({
  content: '□',
  headingObjectStyleType: 'heading',
  headingType,
  id: undefined,
  numberOfOccupyingCharacters: null as null | number | undefined,
  numberOfOccupyingColumns: null,
  numberOfOccupyingRows: null,
  orientation,
  taggedContent: '<p>□</p>',
  useDesignedHeading: false
})

const HeadingContentMoveField = ({ article, layoutable, name, formEditable }: HeadingContentMoveFieldProps) => {
  // 見出し内容を他の種類へ移動するドロップダウンメニューフィールドコンポーネント

  const [field, , helpers] = useField<HeadingItem>(name)
  const context = useFormikContext<FormValues>()
  const selectRef = useRef<HTMLSelectElement>(null)

  const heading = field.value
  const headings = context.values.headings

  const onChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const target = e.target.options[e.target.selectedIndex]
    const selectedFieldName = `headings.${target.value}`

    if (selectedFieldName === name) {
      return
    }

    const orientation =
      target.value === headings.horizontal_designed.base.menuType
        ? Orientation.Horizontal
        : target.value === headings.vertical_designed.base.menuType
          ? Orientation.Vertical
          : heading.data?.orientation
    const headingType = headings[target.value].base.type
    const isCovering = coveringHeadingTypes.has(headingType)

    const numberOfOccupyingColumns =
      !normalHeadingTypes.has(headingType) && !isCovering ? (heading.data?.numberOfOccupyingColumns ?? '') : ''
    const numberOfOccupyingRows =
      !normalHeadingTypes.has(headingType) && !isCovering ? (heading.data?.numberOfOccupyingRows ?? '') : ''
    const numberOfOccupyingCharacters = isCovering
      ? getCoveringMarginCharacters(layoutable?.setting, article?.headingNumberOfOccupyingColumns ?? 0)
      : ''

    context.setFieldValue(`${selectedFieldName}.data`, {
      ...heading.data,
      orientation,
      headingType,
      numberOfOccupyingColumns,
      numberOfOccupyingRows,
      numberOfOccupyingCharacters
    })
    helpers.setValue({ data: undefined, base: heading.base })

    if (selectRef.current !== null) {
      selectRef.current.value = heading.base.menuType
    }
  }

  const options = Object.entries(headings).map(([_name, item]) => {
    const isMe = item.base.menuType === heading.base.menuType
    const optionDisabled = item.data !== undefined
    return (
      <option key={item.base.menuType} value={item.base.menuType} disabled={optionDisabled}>
        {item.base.tagText + (isMe ? '' : 'へ移動')}
      </option>
    )
  })

  return (
    <div id={camelCase(`headingTypeSelect_${name}`.replace('.', '_'))}>
      <DirtyEffect dirty={false}>
        <Form.Select
          size='sm'
          name='headingType'
          defaultValue={heading.base.menuType}
          onChange={onChange}
          disabled={!formEditable || heading.data === undefined}
          ref={selectRef}
        >
          {options}
        </Form.Select>
      </DirtyEffect>
    </div>
  )
}

const HeadingContentEditField = ({ disabled, name, layoutable }: HeadingContentEditFieldProps) => {
  // 見出し内容のスタイル付きテキスト編集のフィールドコンポーネント
  // コンテンツが存在しない場合の非表示は親コンポーネントに依存している

  // Formik resetを捕捉するための仕組み
  // "元に戻す" で TagEditor をリセットさせるためのもの
  const { resetCount } = useFormikReset()

  const validationBase = Yup.string().min(1, '1文字以上にしてください')
  const [, meta, helpers] = useField({
    name: fieldNameWithParent('data.content', name),
    validate: createSimpleValidation(validationBase)
  })
  const [taggedField, taggedMeta, taggedHellper] = useField({
    name: fieldNameWithParent('data.taggedContent', name)
  })

  const isDirty = taggedField.value !== taggedMeta.initialValue

  const onChange = (taggedContent: string, _old: string | undefined) => {
    const content = stripTags(taggedContent)
    helpers.setValue(content, true)
    taggedHellper.setValue(taggedContent, true)
  }

  // TagEditorが完全なFormik(HTML Form)互換ではないためForm.Control.Feedbackが使えない
  const validationError =
    meta.touched && meta.error ? <div className='invalid-feedback d-block'>{meta.error}</div> : undefined

  return (
    <DirtyEffect dirty={isDirty}>
      <div id={camelCase(`tagEditorFor_${name}`.replace('.', '_'))}>
        <TagEditor
          key={resetCount}
          initialTaggedContent={taggedField.value}
          styleTagType='heading'
          setting={layoutable.setting}
          onSubmit={(_e) => undefined}
          onChange={onChange}
          disabled={disabled}
          editable={!disabled}
          // TODO: allow heading text edit after layout lock in future
          // disabled={!isSelectedLayoutableWritable}
          allowLineBreak
          allowMultiParagraph={false}
          appearance='withoutSubmit'
        />
      </div>
      {validationError}
    </DirtyEffect>
  )
}

const HeadingNoContentField = (_props?: Record<string, unknown>) => {
  // 見出し内容が設定されていないときに表示するフィールドコンポーネント
  return <Form.Control size='sm' plaintext readOnly defaultValue='設定なし' />
}

export const HeadingItemEditField = ({ name, article, layoutable, formEditable }: Props) => {
  // 各見出し要素の編集コンポーネント

  const [field, meta, helpers] = useField<HeadingItem>(name)
  const context = useFormikContext<FormValues>()

  const heading = field.value
  const hasData = heading.data !== undefined
  const hasContent = hasData && heading?.data?.taggedContent !== undefined
  const isDirty = context.dirty && (heading?.data === undefined) !== (meta.initialValue?.data === undefined)
  const [collapseOpen, setCollapseOpen] = useState(false)

  const headingContentField = hasContent ? (
    <HeadingContentEditField
      disabled={!formEditable}
      {...{
        article,
        name,
        layoutable
      }}
    />
  ) : (
    <HeadingNoContentField />
  )

  const insertHeadingClicked = (_e: React.MouseEvent<HTMLButtonElement>) => {
    const orientation = ['horizontal_designed'].includes(heading.base.menuType) ? 'horizontal' : 'vertical'
    const headingType = heading.base.type
    const isCovering = coveringHeadingTypes.has(headingType)
    const blankData = blankHeadingData(headingType, orientation)
    if (isCovering) {
      blankData.numberOfOccupyingCharacters = getCoveringMarginCharacters(
        layoutable?.setting,
        article?.headingNumberOfOccupyingColumns ?? 0
      )
    }
    helpers.setValue({ data: blankData, base: heading.base })
  }

  const removeHeadingClicked = (_e: React.MouseEvent<HTMLButtonElement>) => {
    helpers.setValue({ data: undefined, base: heading.base })
    setCollapseOpen(false)
  }

  const nameForId = kebabCase(name)

  const insertRemoveButton = formEditable ? (
    hasData ? (
      <Button
        size='sm'
        variant='link'
        id={`${nameForId}-remove-button`}
        onClick={removeHeadingClicked}
        disabled={!formEditable || !hasData}
        className='p-0 text-danger'
      >
        <FontAwesomeIcon icon='trash' />
      </Button>
    ) : (
      <Button
        size='sm'
        variant='link'
        id={`${nameForId}-insert-button`}
        onClick={insertHeadingClicked}
        disabled={!formEditable || hasData}
        className='p-0'
      >
        <FontAwesomeIcon icon='plus' />
      </Button>
    )
  ) : undefined

  const collapseId = `collapse-${name}`
  const collapseToggle = collapseOpen ? <FontAwesomeIcon icon='caret-down' /> : <FontAwesomeIcon icon='caret-right' />
  const contentLabel = heading?.data?.content ? (
    <>
      {collapseToggle}
      {`${heading?.data?.content}`}
    </>
  ) : (
    ''
  )
  const isContentDirty = heading?.data?.taggedContent !== meta.initialValue?.data?.taggedContent
  return (
    <Row>
      <Col0>
        <DirtyEffect dirty={isDirty}>
          <Row0>
            <Col xs={3}>
              <HeadingContentMoveField
                {...{
                  name,
                  layoutable,
                  article,
                  formEditable
                }}
              />
            </Col>
            <Col xs='auto' className='d-flex align-items-center'>
              <NoEffect>{insertRemoveButton}</NoEffect>
            </Col>
            <Col
              className='d-flex align-items-center'
              onClick={() => {
                if (hasContent) {
                  setCollapseOpen(!collapseOpen)
                }
              }}
              aria-controls={collapseId}
              aria-expanded={collapseOpen}
            >
              <DirtyEffect dirty={isContentDirty}>{contentLabel}</DirtyEffect>
            </Col>
          </Row0>
          <Row0>
            <Col xs={12}>
              <Collapse in={collapseOpen}>
                <div id={collapseId}>{headingContentField}</div>
              </Collapse>
            </Col>
          </Row0>
        </DirtyEffect>
      </Col0>
    </Row>
  )
}
